import React from 'react';
import {
  getApiBaseFromWindow,
} from '../../../lib/runtimeConfig';

type Summary = {
  analyzedCalls: number;
  avgScore: number;
  avgDurationSeconds: number;
  criticalCalls: number;
};

type SdrRow = {
  name: string;
  analyzedCalls: number;
  avgScore: number;
  criticalCalls: number;
  avgDurationSeconds: number;
};

type Payload = {
  ok: boolean;
  today: Summary;
  week: Summary;
  sdrs: SdrRow[];
  updatedAt?: string;
};

type Props = {
  onClose: () => void;
};

function duration(
  seconds: number,
): string {

  const total =
    Math.max(
      0,
      Math.round(
        Number(seconds) || 0,
      ),
    );

  const minutes =
    Math.floor(
      total / 60,
    );

  const rest =
    total % 60;

  if (minutes <= 0) {
    return `${rest}s`;
  }

  return (
    `${minutes}m ${String(rest)
      .padStart(2, '0')}s`
  );
}

function scoreColor(
  score: number,
): string {

  if (score >= 80) {
    return '#45d483';
  }

  if (score >= 60) {
    return '#f2b84b';
  }

  return '#ff5b6e';
}

function StatCard(
  {
    label,
    value,
    accent,
  }: {
    label: string;
    value: React.ReactNode;
    accent?: string;
  },
) {

  return (
    <div
      style={{
        padding:
          '16px 17px',

        borderRadius:
          13,

        background:
          '#181d25',

        border:
          '1px solid #2a313d',

        minWidth: 0,
      }}
    >
      <div
        style={{
          color:
            '#8993a1',

          fontSize:
            10,

          fontWeight:
            800,

          letterSpacing:
            0.75,

          textTransform:
            'uppercase',
        }}
      >
        {label}
      </div>

      <div
        style={{
          marginTop:
            8,

          color:
            accent
            || '#ffffff',

          fontSize:
            24,

          lineHeight:
            1,

          fontWeight:
            900,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function SectionTitle(
  {
    children,
  }: {
    children:
      React.ReactNode;
  },
) {

  return (
    <div
      style={{
        marginBottom:
          10,

        color:
          '#929ba8',

        fontSize:
          10,

        fontWeight:
          900,

        letterSpacing:
          1.2,
      }}
    >
      {children}
    </div>
  );
}

export function SpaceWorkSdrDashboard(
  {
    onClose,
  }: Props,
) {

  const [
    data,
    setData,
  ] =
    React.useState<
      Payload | null
    >(null);

  const [
    loading,
    setLoading,
  ] =
    React.useState(
      true,
    );

  const [
    error,
    setError,
  ] =
    React.useState<
      string | null
    >(null);


  React.useEffect(
    () => {

      let alive =
        true;

      let current:
        AbortController
        | null =
          null;


      const load =
        async () => {

          current
            ?.abort();

          const controller =
            new AbortController();

          current =
            controller;

          const timeout =
            window.setTimeout(
              () =>
                controller.abort(),
              10000,
            );

          try {

            if (!data) {
              setLoading(
                true,
              );
            }

            const apiBase =
              getApiBaseFromWindow();

            const response =
              await fetch(
                `${apiBase}/space-work/sdr-metrics`,
                {
                  credentials:
                    'include',

                  headers: {
                    Accept:
                      'application/json',
                  },

                  signal:
                    controller.signal,
                },
              );

            if (
              !response.ok
            ) {
              throw new Error(
                `HTTP ${response.status}`,
              );
            }

            const payload =
              await response
                .json() as Payload;

            if (
              !payload?.ok
            ) {
              throw new Error(
                'Resposta SDR inválida',
              );
            }

            if (!alive) {
              return;
            }

            setData(
              payload,
            );

            setError(
              null,
            );

          } catch (
            loadError
          ) {

            if (!alive) {
              return;
            }

            setError(
              loadError
                instanceof Error
                  ? loadError
                      .message
                  : 'Falha ao carregar SDR',
            );

          } finally {

            window
              .clearTimeout(
                timeout,
              );

            if (alive) {
              setLoading(
                false,
              );
            }
          }
        };


      void load();


      const interval =
        window.setInterval(
          () => {
            void load();
          },
          30000,
        );


      return () => {

        alive =
          false;

        current
          ?.abort();

        window
          .clearInterval(
            interval,
          );
      };

    },
    [],
  );


  const updatedAt =
    data?.updatedAt
      ? new Date(
          data.updatedAt,
        ).toLocaleTimeString(
          'pt-BR',
          {
            hour:
              '2-digit',

            minute:
              '2-digit',

            second:
              '2-digit',
          },
        )
      : null;


  return (
    <div
      onMouseDown={
        event =>
          event.target
            ===
            event.currentTarget
          &&
          onClose()
      }

      style={{
        position:
          'fixed',

        inset: 0,
        zIndex:
          10000,

        display:
          'grid',

        placeItems:
          'center',

        background:
          'rgba(4,7,11,.74)',

        backdropFilter:
          'blur(8px)',

        padding:
          20,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"

        style={{
          width:
            'min(760px, calc(100vw - 32px))',

          maxHeight:
            'calc(100vh - 48px)',

          overflowY:
            'auto',

          color:
            '#fff',

          background:
            '#0f141b',

          border:
            '1px solid #2a313d',

          borderRadius:
            18,

          boxShadow:
            '0 30px 90px rgba(0,0,0,.55)',
        }}
      >
        <div
          style={{
            display:
              'flex',

            justifyContent:
              'space-between',

            alignItems:
              'flex-start',

            gap:
              20,

            padding:
              20,

            borderBottom:
              '1px solid #252b34',
          }}
        >
          <div>

            <div
              style={{
                color:
                  '#e32346',

                fontSize:
                  10,

                fontWeight:
                  900,

                letterSpacing:
                  1.3,
              }}
            >
              SPACE WORK
            </div>

            <div
              style={{
                marginTop:
                  5,

                fontSize:
                  22,

                fontWeight:
                  800,
              }}
            >
              Painel SDR / Pré-Vendas
            </div>

            <div
              style={{
                marginTop:
                  5,

                color:
                  '#929ba8',

                fontSize:
                  13,
              }}
            >
              Operação + Qualidade IA
            </div>

          </div>

          <button
            type="button"
            onClick={
              onClose
            }

            style={{
              width:
                34,

              height:
                34,

              flex:
                '0 0 auto',

              cursor:
                'pointer',

              color:
                '#fff',

              background:
                '#1b2028',

              border:
                '1px solid #303744',

              borderRadius:
                9,
            }}
          >
            ×
          </button>
        </div>


        <div
          style={{
            padding:
              20,
          }}
        >

          {loading
            && !data
            ? (
              <div
                style={{
                  padding:
                    '36px 10px',

                  textAlign:
                    'center',

                  color:
                    '#929ba8',
                }}
              >
                Carregando métricas SDR…
              </div>
            )
            : null}


          {error
            ? (
              <div
                style={{
                  marginBottom:
                    16,

                  padding:
                    '11px 13px',

                  color:
                    '#ff9aa7',

                  background:
                    'rgba(227,35,70,.08)',

                  border:
                    '1px solid rgba(227,35,70,.28)',

                  borderRadius:
                    10,

                  fontSize:
                    12,
                }}
              >
                Dados SDR:
                {' '}
                {error}
              </div>
            )
            : null}


          {data
            ? (
              <>
                <SectionTitle>
                  HOJE
                </SectionTitle>

                <div
                  style={{
                    display:
                      'grid',

                    gridTemplateColumns:
                      'repeat(4, minmax(0, 1fr))',

                    gap:
                      10,
                  }}
                >
                  <StatCard
                    label="Calls analisadas"
                    value={
                      data.today
                        .analyzedCalls
                    }
                  />

                  <StatCard
                    label="Nota média IA"
                    value={
                      data.today
                        .avgScore
                        .toFixed(1)
                    }
                    accent={
                      scoreColor(
                        data.today
                          .avgScore,
                      )
                    }
                  />

                  <StatCard
                    label="Críticas < 60"
                    value={
                      data.today
                        .criticalCalls
                    }
                    accent={
                      data.today
                        .criticalCalls
                        > 0
                        ? '#ff5b6e'
                        : '#45d483'
                    }
                  />

                  <StatCard
                    label="Duração média"
                    value={
                      duration(
                        data.today
                          .avgDurationSeconds,
                      )
                    }
                  />
                </div>


                <div
                  style={{
                    height:
                      22,
                  }}
                />


                <SectionTitle>
                  SEMANA
                </SectionTitle>

                <div
                  style={{
                    display:
                      'grid',

                    gridTemplateColumns:
                      'repeat(4, minmax(0, 1fr))',

                    gap:
                      10,
                  }}
                >
                  <StatCard
                    label="Calls analisadas"
                    value={
                      data.week
                        .analyzedCalls
                    }
                  />

                  <StatCard
                    label="Nota média IA"
                    value={
                      data.week
                        .avgScore
                        .toFixed(1)
                    }
                    accent={
                      scoreColor(
                        data.week
                          .avgScore,
                      )
                    }
                  />

                  <StatCard
                    label="Críticas < 60"
                    value={
                      data.week
                        .criticalCalls
                    }
                    accent={
                      data.week
                        .criticalCalls
                        > 0
                        ? '#ff5b6e'
                        : '#45d483'
                    }
                  />

                  <StatCard
                    label="Duração média"
                    value={
                      duration(
                        data.week
                          .avgDurationSeconds,
                      )
                    }
                  />
                </div>


                <div
                  style={{
                    height:
                      24,
                  }}
                />


                <SectionTitle>
                  PERFORMANCE POR SDR
                </SectionTitle>

                <div
                  style={{
                    display:
                      'grid',

                    gap:
                      8,
                  }}
                >
                  {data.sdrs
                    .map(
                      sdr => (

                        <div
                          key={
                            sdr.name
                          }

                          style={{
                            display:
                              'grid',

                            gridTemplateColumns:
                              'minmax(130px, 1.5fr) repeat(4, minmax(80px, 1fr))',

                            alignItems:
                              'center',

                            gap:
                              10,

                            padding:
                              '12px 14px',

                            background:
                              '#181d25',

                            border:
                              '1px solid #2a313d',

                            borderRadius:
                              11,
                          }}
                        >
                          <div>

                            <div
                              style={{
                                fontSize:
                                  14,

                                fontWeight:
                                  800,
                              }}
                            >
                              {sdr.name}
                            </div>

                            <div
                              style={{
                                marginTop:
                                  3,

                                color:
                                  '#717b89',

                                fontSize:
                                  10,
                              }}
                            >
                              semana atual
                            </div>

                          </div>


                          <div>
                            <div
                              style={{
                                fontWeight:
                                  900,
                              }}
                            >
                              {
                                sdr
                                  .analyzedCalls
                              }
                            </div>

                            <div
                              style={{
                                color:
                                  '#717b89',

                                fontSize:
                                  9,
                              }}
                            >
                              CALLS IA
                            </div>
                          </div>


                          <div>
                            <div
                              style={{
                                color:
                                  scoreColor(
                                    sdr.avgScore,
                                  ),

                                fontWeight:
                                  900,
                              }}
                            >
                              {sdr
                                .avgScore
                                .toFixed(1)}
                            </div>

                            <div
                              style={{
                                color:
                                  '#717b89',

                                fontSize:
                                  9,
                              }}
                            >
                              NOTA IA
                            </div>
                          </div>


                          <div>
                            <div
                              style={{
                                color:
                                  sdr
                                    .criticalCalls
                                    > 0
                                    ? '#ff7586'
                                    : '#45d483',

                                fontWeight:
                                  900,
                              }}
                            >
                              {
                                sdr
                                  .criticalCalls
                              }
                            </div>

                            <div
                              style={{
                                color:
                                  '#717b89',

                                fontSize:
                                  9,
                              }}
                            >
                              CRÍTICAS
                            </div>
                          </div>


                          <div>
                            <div
                              style={{
                                fontWeight:
                                  800,
                              }}
                            >
                              {duration(
                                sdr
                                  .avgDurationSeconds,
                              )}
                            </div>

                            <div
                              style={{
                                color:
                                  '#717b89',

                                fontSize:
                                  9,
                              }}
                            >
                              DURAÇÃO
                            </div>
                          </div>

                        </div>
                      ),
                    )}
                </div>


                <div
                  style={{
                    marginTop:
                      16,

                    display:
                      'flex',

                    justifyContent:
                      'space-between',

                    gap:
                      10,

                    flexWrap:
                      'wrap',

                    color:
                      '#717b89',

                    fontSize:
                      10,
                  }}
                >
                  <span>
                    Calls IA = gravações processadas
                    pelo scoring.
                  </span>

                  <span>
                    Atualização automática
                    a cada 30s
                    {updatedAt
                      ? ` • ${updatedAt}`
                      : ''}
                  </span>
                </div>
              </>
            )
            : null}

        </div>
      </div>
    </div>
  );
}
